import { useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { searchAdminUsers } from '../api/endpoints';

/**
 * SHAR-108 — a searchable dropdown of customers: the AirportSelect pattern (a
 * debounced remote search from two characters, the options straight from the
 * API), matched by email, first/last name or "First Last".
 */
export function UserSelect({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (id: string, user?: { id: string; email: string; name: string | null }) => void;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const lookup = useQuery({
    queryKey: ['admin-user-search', debounced],
    queryFn: () => searchAdminUsers(debounced),
    enabled: debounced.trim().length >= 2,
  });

  const options = useMemo(
    () =>
      (lookup.data || []).map((u) => {
        const text = u.name ? `${u.name} — ${u.email}` : u.email;
        // PO, 30 Sep — the full name and email on hover, so two long emails
        // with the same start can be told apart; the option stays truncated.
        return {
          value: u.id,
          title: text,
          label: (
            <span title={text} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </span>
          ),
        };
      }),
    [lookup.data],
  );

  return (
    <Select
      showSearch
      placeholder="Start typing — name or email"
      value={value}
      filterOption={false}
      onSearch={setQ}
      onChange={(v) => onChange?.(v, (lookup.data || []).find((u) => u.id === v))}
      notFoundContent={lookup.isFetching ? <Spin size="small" /> : debounced.trim().length >= 2 ? 'No customers found' : null}
      options={options}
      style={{ width: '100%' }}
    />
  );
}
